
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import type { TimeRecord } from '@/types';
import type { LocationData } from './useLocation';

export const useClockInOut = (
  setActiveTimeRecord: (record: TimeRecord | null) => void,
  fetchTodaysVisits: () => void
) => {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(false);

  // Helper function to validate and transform location data
  const transformLocationData = (locationJson: any): { lat: number; lng: number; address?: string } | undefined => {
    if (!locationJson || typeof locationJson !== 'object' || Array.isArray(locationJson)) {
      return undefined;
    }
    
    const { lat, lng, address } = locationJson;
    
    if (typeof lat === 'number' && typeof lng === 'number') {
      return {
        lat,
        lng,
        ...(typeof address === 'string' && { address })
      };
    }
    
    return undefined;
  };

  const handleClockIn = async (visitId: string, location: LocationData) => {
    if (!profile) return;

    setLoading(true);
    try {
      // Create time record
      const timeRecordData = {
        visit_id: visitId,
        nurse_id: profile.id,
        clock_in_time: new Date().toISOString(),
        clock_in_location: {
          lat: location.lat,
          lng: location.lng,
          timestamp: new Date().toISOString()
        }
      };

      const { data, error } = await supabase
        .from('time_records')
        .insert(timeRecordData)
        .select()
        .single();

      if (error) throw error;

      // Update visit status
      await supabase
        .from('visits')
        .update({
          status: 'in_progress',
          actual_start_time: new Date().toISOString()
        })
        .eq('id', visitId);

      // Transform and set active time record with proper type validation
      const transformedRecord: TimeRecord = {
        ...data,
        clock_in_location: transformLocationData(data.clock_in_location),
        clock_out_location: transformLocationData(data.clock_out_location)
      };
      
      setActiveTimeRecord(transformedRecord);
      toast.success('Clocked in successfully!');
      
      // Refresh visits
      fetchTodaysVisits();

    } catch (error) {
      console.error('Error clocking in:', error);
      if (error instanceof Error && error.message.includes('location')) {
        toast.error('Location access required for clock in. Please enable GPS.');
      } else {
        toast.error('Failed to clock in');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClockOut = async (activeTimeRecord: TimeRecord, location: LocationData) => {
    if (!activeTimeRecord) return;

    setLoading(true);
    try {
      const clockInTime = new Date(activeTimeRecord.clock_in_time);
      const clockOutTime = new Date();
      const totalMinutes = Math.floor((clockOutTime.getTime() - clockInTime.getTime()) / 60000);

      // Update time record
      const { error } = await supabase
        .from('time_records')
        .update({
          clock_out_time: clockOutTime.toISOString(),
          clock_out_location: {
            lat: location.lat,
            lng: location.lng,
            timestamp: clockOutTime.toISOString()
          },
          total_minutes: totalMinutes
        })
        .eq('id', activeTimeRecord.id);

      if (error) throw error;

      // Update visit status
      await supabase
        .from('visits')
        .update({
          status: 'completed',
          actual_end_time: clockOutTime.toISOString()
        })
        .eq('id', activeTimeRecord.visit_id);

      setActiveTimeRecord(null);
      toast.success(`Clocked out successfully! Total time: ${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`);
      
      // Refresh visits
      fetchTodaysVisits();

    } catch (error) {
      console.error('Error clocking out:', error);
      if (error instanceof Error && error.message.includes('location')) {
        toast.error('Location access required for clock out. Please enable GPS.');
      } else {
        toast.error('Failed to clock out');
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    handleClockIn,
    handleClockOut
  };
};
